import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * WOLF — Cinematic Intro Overlay
 * Plays once per session: black canvas → wolf eye pulses gold → WOLF wordmark "foils in"
 * with a shimmer sweep → tagline → fade out. ~6 seconds total.
 */
export default function IntroOverlay() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const seen = sessionStorage.getItem("wolf:intro:seen");
      if (!seen) {
        setShow(true);
        sessionStorage.setItem("wolf:intro:seen", "1");
      }
    } catch (_) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => setShow(false), 6200);
    return () => clearTimeout(t);
  }, [show]);

  const skip = () => setShow(false);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="wolf-intro"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="fixed inset-0 z-[200] bg-black flex items-center justify-center overflow-hidden cursor-pointer"
          onClick={skip}
          data-testid="intro-overlay"
        >
          {/* Subtle radial gold haze backdrop */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 65%)",
            }}
          />

          {/* Floating dust */}
          <div className="dust-particles opacity-50" />

          {/* The eye — golden pulse */}
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{
              opacity: [0, 1, 1, 0.0],
              scale: [0.4, 1, 1.15, 1.6],
            }}
            transition={{
              duration: 2.4,
              times: [0, 0.35, 0.85, 1],
              ease: "easeInOut",
            }}
            className="absolute"
            data-testid="intro-eye"
          >
            <div
              className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, #FFE08A 0%, #F4C542 28%, #D4AF37 55%, rgba(212,175,55,0) 75%)",
                boxShadow:
                  "0 0 28px 6px rgba(244, 197, 66, 0.75), 0 0 80px 18px rgba(212, 175, 55, 0.45), 0 0 160px 40px rgba(122, 90, 24, 0.30)",
              }}
            />
          </motion.div>

          {/* The wordmark — foils in with shimmer sweep */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 2.0, duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
            className="relative z-10 text-center px-6"
          >
            <h1
              data-testid="intro-wordmark"
              className="font-display intro-foil tracking-tighter leading-none text-[28vw] sm:text-[18vw] lg:text-[14rem]"
            >
              WOLF
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.6, duration: 1.0 }}
              className="font-editorial italic text-[#D4AF37] text-xl sm:text-3xl mt-2 sm:mt-4"
              data-testid="intro-tagline"
            >
              Forged in the dark. Still standing.
            </motion.p>
          </motion.div>

          {/* Skip */}
          <button
            type="button"
            onClick={skip}
            data-testid="intro-skip-btn"
            className="absolute bottom-6 right-6 sm:bottom-8 sm:right-10 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/70 hover:text-[#D4AF37] border-b border-[#D4AF37]/30 pb-1"
          >
            Skip
          </button>

          {/* Vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.85) 100%)",
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
